/* global axios */
// Softphone AstraCall — Central de Ligações ao vivo (Fase 1: discar/atender/encerrar
// áudio, com base pronta p/ hold/transferência/vídeo). Browser-direct (o call server
// tem CORS liberado e SSE por apiKey). Um RTCPeerConnection por call_id.
import { reactive, computed } from 'vue';

// estado singleton (compartilhado por todo o painel)
const state = reactive({
  ready: false,
  enabled: false,
  baseUrl: '',
  apiKey: '',
  clientId: '',
  sessions: [], // [{ sid, name, inbox_id, inbox_name }]
  calls: {}, // por callId: { callId, sid, status, direction, peer, held, startedAt, muted }
  // callIds com ponte de áudio WebRTC ATIVA neste navegador (reativo p/ os getters).
  // Só chamadas aqui contam como "ativas" no dock — evita hidratar fantasmas do
  // call-list (chamadas presas no call server sem call-ended) como se fossem desta aba.
  bridged: {},
});

const peers = {}; // callId -> { pc, audioEl, localStream }
let es = null;
let initPromise = null;

const CLIENT_KEY = 'astracall_client_id';
const uuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

const getClientId = () => {
  let id = localStorage.getItem(CLIENT_KEY);
  if (!id) {
    id = uuid();
    localStorage.setItem(CLIENT_KEY, id);
  }
  return id;
};

// ---- REST direto no call server ----
const rest = async (sid, path, { method = 'POST', body } = {}) => {
  const res = await fetch(`${state.baseUrl}/api/sessions/${sid}/${path}`, {
    method,
    headers: {
      'X-API-Key': state.apiKey,
      'X-Client-Id': state.clientId,
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
};

// ---- estado das chamadas ----
const upsertCall = c => {
  const id = c.callId || c.id;
  if (!id) return;
  state.calls[id] = { ...(state.calls[id] || {}), ...c, callId: id };
};
const removeCall = id => {
  cleanupPeer(id);
  delete state.calls[id];
};

const cleanupPeer = id => {
  delete state.bridged[id];
  const p = peers[id];
  if (!p) return;
  try {
    p.localStream?.getTracks().forEach(t => t.stop());
    p.pc?.close();
    p.audioEl?.remove();
  } catch (e) {
    /* noop */
  }
  delete peers[id];
};

// ---- WebRTC: abre/troca a ponte de áudio de uma chamada ----
const openBridge = async (sid, callId, { video = false } = {}) => {
  cleanupPeer(callId);
  const localStream = await navigator.mediaDevices.getUserMedia({
    audio: true,
    video,
  });
  const pc = new RTCPeerConnection({
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
  });
  localStream.getTracks().forEach(t => pc.addTrack(t, localStream));

  const audioEl = document.createElement('audio');
  audioEl.autoplay = true;
  audioEl.dataset.astracall = callId;
  document.body.appendChild(audioEl);
  pc.ontrack = e => {
    audioEl.srcObject = e.streams[0];
  };

  peers[callId] = { pc, audioEl, localStream };
  state.bridged[callId] = true;

  const offer = await pc.createOffer({ offerToReceiveAudio: true });
  await pc.setLocalDescription(offer);
  const { sdp_answer: answer } = await rest(sid, `calls/${callId}/webrtc`, {
    body: { sdp_offer: offer.sdp },
  });
  await pc.setRemoteDescription({ type: 'answer', sdp: answer });
};

// ---- SSE ----
const handleEvent = msg => {
  switch (msg.type) {
    case 'call-list':
      (msg.calls || []).forEach(upsertCall);
      break;
    case 'call-status':
      upsertCall({ ...msg, callId: msg.id });
      break;
    case 'incoming':
      upsertCall({
        callId: msg.id,
        sid: msg.sessionId,
        status: 'ringing',
        direction: 'incoming',
        peer: msg.peer,
        video: msg.video,
      });
      break;
    case 'incoming-claimed':
      // outro atendente atendeu
      if (state.calls[msg.id] && state.calls[msg.id].owner !== state.clientId)
        removeCall(msg.id);
      break;
    case 'call-ended':
      logEndedCall(state.calls[msg.id], msg);
      removeCall(msg.id);
      break;
    default:
      break;
  }
};

const openStream = () => {
  if (es) es.close();
  const url = `${state.baseUrl}/api/events?clientId=${encodeURIComponent(
    state.clientId
  )}&apiKey=${encodeURIComponent(state.apiKey)}`;
  es = new EventSource(url);
  es.onmessage = ev => {
    try {
      handleEvent(JSON.parse(ev.data));
    } catch (e) {
      /* noop */
    }
  };
  es.onerror = () => {
    /* EventSource reconecta sozinho */
  };
};

// registra a ligação encerrada no histórico (Central de Ligações)
const logEndedCall = (call, ended) => {
  if (!call) return;
  const started = call.startedAt ? new Date(call.startedAt) : null;
  const dur = started ? Math.max(0, Math.round((Date.now() - started) / 1000)) : 0;
  const session = state.sessions.find(s => s.sid === call.sid);
  const answered = !!call.startedAt && call.status === 'connected';
  axios
    .post(`${state.accountBase}/kanban/call_history`, {
      conversation_id: call.conversation_id || null,
      contact_phone: call.peer || call.phone || '',
      contact_name: call.contact_name || '',
      direction: call.direction === 'incoming' ? 'INCOMING' : 'OUTGOING',
      status: answered ? 'ENDED' : 'NOT_ANSWERED',
      instance_name: session?.name || '',
      inbox_id: session?.inbox_id || null,
      duration_seconds: dur,
      call_method: 'astracall',
    })
    .catch(() => {});
};

// ---- init ----
const ensureInit = async accountId => {
  if (state.ready) return state.enabled;
  if (initPromise) return initPromise;
  state.accountBase = `/api/v1/accounts/${accountId}`;
  initPromise = (async () => {
    try {
      const { data } = await axios.get(`${state.accountBase}/kanban/astracalls/config`);
      const cfg = data.data || {};
      state.enabled = !!cfg.enabled;
      state.baseUrl = cfg.base_url || '';
      state.apiKey = cfg.api_key || '';
      state.sessions = cfg.sessions || [];
      state.clientId = getClientId();
      if (state.enabled) openStream();
    } catch (e) {
      state.enabled = false;
    } finally {
      state.ready = true;
    }
    return state.enabled;
  })();
  return initPromise;
};

// ---- ações públicas ----
const dial = async ({ sid, phone, conversationId, contactName }) => {
  const { call } = await rest(sid, 'calls', { body: { phone } });
  const callId = call.callId;
  upsertCall({
    callId,
    sid,
    status: 'starting',
    direction: 'outgoing',
    peer: phone,
    conversation_id: conversationId,
    contact_name: contactName,
  });
  await openBridge(sid, callId, {});
  return callId;
};

const accept = async call => {
  await rest(call.sid, `calls/${call.callId}/accept`);
  await openBridge(call.sid, call.callId, {});
};
const reject = async call => {
  await rest(call.sid, `calls/${call.callId}/reject`).catch(() => {});
  removeCall(call.callId);
};
const hangup = async call => {
  await rest(call.sid, `calls/${call.callId}`, { method: 'DELETE' }).catch(() => {});
  removeCall(call.callId);
};
const toggleMute = call => {
  const p = peers[call.callId];
  if (!p) return;
  const muted = !call.muted;
  p.localStream.getAudioTracks().forEach(t => {
    t.enabled = !muted;
  });
  upsertCall({ callId: call.callId, muted });
};

// ---- getters ----
const callList = computed(() => Object.values(state.calls));
// Só é "ativa" a chamada cuja ponte de áudio foi aberta NESTA aba (dial/accept).
// Assim, chamadas presas no call server que chegam via call-list (fantasmas, sem
// call-ended) não reaparecem travadas no dock a cada reload nem vazam de outro atendente.
const activeCalls = computed(() =>
  callList.value.filter(
    c =>
      (c.direction !== 'incoming' || c.status !== 'ringing') && !!state.bridged[c.callId]
  )
);
const incomingCalls = computed(() =>
  callList.value.filter(c => c.direction === 'incoming' && c.status === 'ringing')
);

export function useAstraCalls() {
  return {
    state,
    ensureInit,
    dial,
    accept,
    reject,
    hangup,
    toggleMute,
    callList,
    activeCalls,
    incomingCalls,
    sessionForInbox: inboxId =>
      state.sessions.find(s => String(s.inbox_id) === String(inboxId)),
  };
}
